import { resolveEffectiveVenuePlan } from './invoiceUploadQueueHelpers'
import {
  VENUE_ADMIN,
  HEAD_CHEF_GM,
  CHEF,
  ORDER_ONLY,
  KITCHEN_PORTER,
} from './roles'
import {
  VENUE_ROLE_CAPABILITY,
  hasCapability,
  isCustomRoleId,
  normalizeDashboardVenueRole,
  normalizeSystemRoleId,
  resolveVenueRole,
} from './venueRoleCapabilities'

/**
 * Permission matrix defining which system roles can access which screens/features
 * Key: screen identifier
 * Value: array of roles that can access this screen
 *
 * Custom roles do not use this list — they follow capability ticks.
 */
export const PERMISSION_MATRIX = {
  // Purchasing screens
  'gross-profit': [VENUE_ADMIN, HEAD_CHEF_GM],
  receipting: [VENUE_ADMIN, HEAD_CHEF_GM],
  invoices: [VENUE_ADMIN, HEAD_CHEF_GM, CHEF, ORDER_ONLY],
  'spend-over-time': [VENUE_ADMIN, HEAD_CHEF_GM],
  'credit-notes': [VENUE_ADMIN, HEAD_CHEF_GM],
  'resolved-notes': [VENUE_ADMIN, HEAD_CHEF_GM],
  'product-issues': [VENUE_ADMIN, HEAD_CHEF_GM],
  'xero-integration': [VENUE_ADMIN],
  statement: [VENUE_ADMIN, HEAD_CHEF_GM, CHEF],
  'ordered-products': [VENUE_ADMIN, HEAD_CHEF_GM, CHEF],

  // Core features
  orders: [VENUE_ADMIN, HEAD_CHEF_GM, CHEF, ORDER_ONLY],
  // Dashboard order approvals area (pending restricted-supplier approvals)
  approvals: [VENUE_ADMIN, HEAD_CHEF_GM],
  'shopping-lists': [VENUE_ADMIN, HEAD_CHEF_GM, CHEF, ORDER_ONLY],
  basket: [VENUE_ADMIN, HEAD_CHEF_GM, CHEF, ORDER_ONLY],
  catalogue: [VENUE_ADMIN, HEAD_CHEF_GM, CHEF],
  suppliers: [VENUE_ADMIN, HEAD_CHEF_GM, CHEF, ORDER_ONLY],
  'menu-builder': [VENUE_ADMIN, HEAD_CHEF_GM, CHEF],
  stock: [VENUE_ADMIN, HEAD_CHEF_GM, CHEF],
  // Role does not gate Special Purchases — Pro plan does (see canSeeSpecialPurchasesTab).
  'special-purchases': [VENUE_ADMIN, HEAD_CHEF_GM, CHEF, ORDER_ONLY],

  // Admin-only features
  subscriptions: [VENUE_ADMIN],
  integrations: [VENUE_ADMIN],
  settings: [VENUE_ADMIN],
  'settings-general': [VENUE_ADMIN, HEAD_CHEF_GM],
  'settings-users': [VENUE_ADMIN, HEAD_CHEF_GM],
  'settings-notifications': [VENUE_ADMIN, HEAD_CHEF_GM, CHEF],
  // Food Safety — complete is broad; manage is Admin / Head Chef GM
  'food-safety': [VENUE_ADMIN, HEAD_CHEF_GM, CHEF, ORDER_ONLY, KITCHEN_PORTER],
  'food-safety-manage': [VENUE_ADMIN, HEAD_CHEF_GM],
}

export const STOCK_VARIANCE_VISIBLE_ROLES = [VENUE_ADMIN, HEAD_CHEF_GM, 'ADMIN']

/** Stock table column keys hidden from Chef and other restricted roles. */
export const STOCK_SENSITIVE_COLUMN_KEYS = ['expectedQty', 'variance']

/**
 * Omit expected qty / variance columns when the role cannot view them.
 * @param {Array<{ key?: string }>} columns
 * @param {boolean} canViewExpectedAndVariance
 */
export const filterStockTableColumns = (columns, canViewExpectedAndVariance) =>
  (columns || []).filter(
    column =>
      canViewExpectedAndVariance ||
      (column.key !== 'expectedQty' && column.key !== 'variance'),
  )

/**
 * @param {Array<{ key?: string }>} columns
 * @param {string} role
 */
export const filterStockTableColumnsForRole = (
  columns,
  role,
  customRoles = [],
) => {
  const { canViewExpectedAndVariance } = getStockRoleCapabilities(
    role,
    customRoles,
  )
  return filterStockTableColumns(columns, canViewExpectedAndVariance)
}

/** Roles that may unlock a locked stock take (system roles; custom roles use the stockUnlock tick). */
export const STOCK_UNLOCK_ROLES = [VENUE_ADMIN]

/**
 * Normalise role strings for stock permission checks.
 * Preserves custom role ids. ADMIN is treated as VENUE_ADMIN.
 */
export const normalizeStockRole = role => normalizeDashboardVenueRole(role)

/**
 * Single source of truth for what a role can do on Stock.
 * System roles use the same capability ticks as custom roles.
 * @param {string} role
 * @param {Array} [customRoles=[]]
 * @returns {{
 *   role: string,
 *   roleDisplayName: string,
 *   canAccessStock: boolean,
 *   canViewExpectedAndVariance: boolean,
 *   hideSensitiveStockColumns: boolean,
 *   canLockStockTake: boolean,
 *   canUnlockStockTake: boolean,
 *   canEditCountsWhenUnlocked: boolean,
 * }}
 */
export const getStockRoleCapabilities = (role, customRoles = []) => {
  const normalizedRole = normalizeStockRole(role)
  const resolved = resolveVenueRole(normalizedRole, customRoles)
  const canAccessStock = hasCapability(
    normalizedRole,
    VENUE_ROLE_CAPABILITY.STOCK_COUNTS,
    customRoles,
  )
  const canViewExpectedAndVariance = hasCapability(
    normalizedRole,
    VENUE_ROLE_CAPABILITY.STOCK_SENSITIVE,
    customRoles,
  )
  const canUnlock = hasCapability(
    normalizedRole,
    VENUE_ROLE_CAPABILITY.STOCK_UNLOCK,
    customRoles,
  )

  return {
    role: resolved.isFallback ? resolved.id : normalizedRole,
    roleDisplayName: resolved.displayName,
    canAccessStock,
    canViewExpectedAndVariance,
    hideSensitiveStockColumns: !canViewExpectedAndVariance,
    canLockStockTake: canAccessStock,
    canUnlockStockTake: canUnlock,
    canEditCountsWhenUnlocked: canAccessStock,
  }
}

/**
 * Human-readable access bullets for the Stock tab (StockRoleAccessCard, step 9).
 * @param {string} role
 * @param {Array} [customRoles=[]]
 * @returns {Array<{ key: string, label: string, allowed: boolean }>}
 */
export const getStockRoleAccessBullets = (role, customRoles = []) => {
  const capabilities = getStockRoleCapabilities(role, customRoles)

  return [
    {
      key: 'canAccessStock',
      label: 'Access Stock',
      allowed: capabilities.canAccessStock,
    },
    {
      key: 'canEditCountsWhenUnlocked',
      label: 'Enter counts while stock take is unlocked',
      allowed: capabilities.canEditCountsWhenUnlocked,
    },
    {
      key: 'canViewExpectedAndVariance',
      label: 'View expected quantities and variance',
      allowed: capabilities.canViewExpectedAndVariance,
    },
    {
      key: 'canLockStockTake',
      label: 'Lock stock takes',
      allowed: capabilities.canLockStockTake,
    },
    {
      key: 'canUnlockStockTake',
      label: 'Unlock stock takes (reason required)',
      allowed: capabilities.canUnlockStockTake,
    },
  ]
}

/**
 * Per-role stock capability matrix for RolePermissionsModal (step 8).
 * Values: true | false | 'limited' (chef-style partial access to Stock).
 */
export const STOCK_ROLE_PERMISSION_ROWS = [
  {
    key: 'stock-access',
    feature: 'Stock — access tab and enter counts',
    admin: true,
    headChefGM: true,
    chef: true,
    orderOnly: false,
  },
  {
    key: 'stock-variance',
    feature: 'Stock — expected qty, variance, valuation sensitive columns',
    admin: true,
    headChefGM: true,
    chef: false,
    orderOnly: false,
  },
  {
    key: 'stock-lock',
    feature: 'Stock — lock stock takes',
    admin: true,
    headChefGM: true,
    chef: true,
    orderOnly: false,
  },
  {
    key: 'stock-unlock',
    feature: 'Stock — unlock stock takes',
    admin: true,
    headChefGM: false,
    chef: false,
    orderOnly: false,
  },
]

const STOCK_ROW_CAPABILITY = {
  'stock-access': VENUE_ROLE_CAPABILITY.STOCK_COUNTS,
  'stock-variance': VENUE_ROLE_CAPABILITY.STOCK_SENSITIVE,
  'stock-lock': VENUE_ROLE_CAPABILITY.STOCK_COUNTS,
  'stock-unlock': VENUE_ROLE_CAPABILITY.STOCK_UNLOCK,
}

/**
 * Whether a stock permission row grants access for the given role.
 * System roles and custom roles both use capability ticks.
 * @param {string} rowKey - STOCK_ROLE_PERMISSION_ROWS[].key
 * @param {string} role
 * @param {Array} [customRoles=[]]
 */
export const stockPermissionRowAllowsRole = (
  rowKey,
  role,
  customRoles = [],
) => {
  const capability = STOCK_ROW_CAPABILITY[rowKey]
  if (!capability) return false
  return hasCapability(normalizeStockRole(role), capability, customRoles)
}

/**
 * Screen → capability tick for custom roles.
 * System roles keep PERMISSION_MATRIX, including cases that differ from ticks
 * (Order Only can open dashboard suppliers; Chef has statement without reports).
 * Invoice upload / the invoices page is unrestricted; GR stays on the invoices tick.
 *
 * settings-users is never granted by ticks — only Venue Admin / Head Chef via the matrix.
 */
export const SCREEN_CAPABILITY = {
  'gross-profit': VENUE_ROLE_CAPABILITY.REPORTS,
  'spend-over-time': VENUE_ROLE_CAPABILITY.REPORTS,
  receipting: VENUE_ROLE_CAPABILITY.INVOICES,
  invoices: VENUE_ROLE_CAPABILITY.INVOICES,
  'credit-notes': VENUE_ROLE_CAPABILITY.CREDIT_NOTES,
  'resolved-notes': VENUE_ROLE_CAPABILITY.CREDIT_NOTES,
  'product-issues': VENUE_ROLE_CAPABILITY.CREDIT_NOTES,
  'xero-integration': VENUE_ROLE_CAPABILITY.SUBSCRIPTIONS,
  integrations: VENUE_ROLE_CAPABILITY.SUBSCRIPTIONS,
  subscriptions: VENUE_ROLE_CAPABILITY.SUBSCRIPTIONS,
  orders: VENUE_ROLE_CAPABILITY.ORDERING,
  approvals: VENUE_ROLE_CAPABILITY.ORDER_APPROVALS,
  'shopping-lists': VENUE_ROLE_CAPABILITY.ORDERING,
  basket: VENUE_ROLE_CAPABILITY.ORDERING,
  catalogue: VENUE_ROLE_CAPABILITY.SUPPLIERS,
  suppliers: VENUE_ROLE_CAPABILITY.SUPPLIERS,
  'menu-builder': VENUE_ROLE_CAPABILITY.MENU_BUILDER,
  stock: VENUE_ROLE_CAPABILITY.STOCK_COUNTS,
  'special-purchases': VENUE_ROLE_CAPABILITY.REPORTS,
  settings: VENUE_ROLE_CAPABILITY.SETTINGS,
  'settings-general': VENUE_ROLE_CAPABILITY.SETTINGS,
  'settings-notifications': VENUE_ROLE_CAPABILITY.SETTINGS,
  statement: VENUE_ROLE_CAPABILITY.ORDERING,
  'ordered-products': VENUE_ROLE_CAPABILITY.ORDERING,
  'food-safety': VENUE_ROLE_CAPABILITY.FOOD_SAFETY_COMPLETE,
  'food-safety-manage': VENUE_ROLE_CAPABILITY.FOOD_SAFETY_MANAGE,
}

const CUSTOM_ROLE_DENIED_SCREENS = ['settings-users']

const systemRoleIdForAccess = role => {
  const direct = normalizeSystemRoleId(role)
  if (direct) return direct
  return normalizeSystemRoleId(
    String(role || '')
      .trim()
      .toUpperCase(),
  )
}

/**
 * Check if a role can access a specific screen/feature
 * @param {string} screen - The screen identifier (e.g., 'gross-profit', 'catalogue')
 * @param {string} role - The user's role
 * @param {Array} [customRoles=[]] - Venue custom role definitions (id + capabilities)
 * @returns {boolean} True if the role can access the screen
 */
export const canAccess = (screen, role, customRoles = []) => {
  // Every role can upload invoices — do not gate the invoices page on the GR tick.
  if (screen === 'invoices') {
    return true
  }

  // Special Purchases is plan-gated (Pro), not role-gated.
  if (screen === 'special-purchases') {
    return true
  }

  // Progress is for every role. Tiles inside the page hide jobs they cannot finish.
  if (screen === 'progress') {
    return true
  }

  // Home is the venue landing for every role.
  if (screen === 'home') {
    return true
  }

  const allowedRoles = PERMISSION_MATRIX[screen]
  if (!allowedRoles) {
    // If screen not in matrix, default to allowing access (for backward compatibility)
    return true
  }

  const systemRoleId = systemRoleIdForAccess(role)
  if (systemRoleId && !isCustomRoleId(role)) {
    return allowedRoles.includes(systemRoleId)
  }

  if (CUSTOM_ROLE_DENIED_SCREENS.includes(screen)) {
    return false
  }

  const capability = SCREEN_CAPABILITY[screen]
  if (!capability) {
    return true
  }

  return hasCapability(role, capability, customRoles)
}

/**
 * Check if a role is blocked from accessing a screen
 * @param {string} screen - The screen identifier
 * @param {string} role - The user's role
 * @param {Array} [customRoles=[]] - Venue custom role definitions (id + capabilities)
 * @returns {boolean} True if the role is blocked
 */
export const isBlocked = (screen, role, customRoles = []) => {
  return !canAccess(screen, role, customRoles)
}

export const canAccessAny = (screens, role, customRoles = []) =>
  (screens || []).some(screen => canAccess(screen, role, customRoles))

/** Purchasing sub-menu: same screen keys as the gated pages. */
export const DASHBOARD_PURCHASING_NAV = [
  { key: 'orders', screen: 'orders', path: 'orders' },
  { key: 'receipting', screen: 'receipting', path: 'receipting' },
  { key: 'invoices', screen: 'invoices', path: 'invoices' },
  { key: 'creditnotes', screen: 'credit-notes', path: 'creditnotes' },
  { key: 'product-issues', screen: 'product-issues', path: 'product-issues' },
  { key: 'gross-profit', screen: 'gross-profit', path: 'gross-profit' },
  { key: 'statement', screen: 'statement', path: 'statement' },
  { key: 'ordered', screen: 'ordered-products', path: 'ordered' },
  { key: 'spendTime', screen: 'spend-over-time', path: 'spendTime' },
]

export const DASHBOARD_SETTINGS_NAV_SCREENS = [
  'settings',
  'settings-general',
  'settings-users',
  'settings-notifications',
  'subscriptions',
  'integrations',
]

export const getDashboardPurchasingHomePath = (
  venueId,
  role,
  customRoles = [],
) => {
  if (!venueId) return null
  const item = DASHBOARD_PURCHASING_NAV.find(({ screen }) =>
    canAccess(screen, role, customRoles),
  )
  return item ? `/v/${venueId}/purchasing/${item.path}` : null
}

export const DASHBOARD_TOP_NAV = [
  { key: 'home', screens: ['home'] },
  {
    key: 'purchasing',
    screens: DASHBOARD_PURCHASING_NAV.map(({ screen }) => screen),
  },
  { key: 'progress', screens: ['progress'] },
  { key: 'suppliers', screens: ['suppliers'] },
  { key: 'catalogue', screens: ['catalogue'] },
  { key: 'products', screens: ['shopping-lists'] },
  { key: 'stockTakes', screens: ['stock'] },
  { key: 'food-safety', screens: ['food-safety'] },
  { key: 'approvals', screens: ['approvals'] },
  { key: 'menu-builder', screens: ['menu-builder'] },
  { key: 'settings', screens: DASHBOARD_SETTINGS_NAV_SCREENS },
]

/**
 * Special Purchases top-nav visibility: Pro plan only (role ignored).
 * Uses Stripe newPricing when present so stale UsageLimits do not hide the tab.
 */
export const canSeeSpecialPurchasesTab = venue =>
  resolveEffectiveVenuePlan({
    newPricing: venue?.newPricing,
    usageLimits: venue?.usageLimits,
    currentPlan: venue?.usageLimits?.currentPlan,
  }) === 'Pro'

export const canSeeDashboardTopNav = (key, role, customRoles = []) => {
  if (key === 'special-purchases') {
    // Role must not control this tab — callers should use canSeeSpecialPurchasesTab(venue).
    return true
  }
  const item = DASHBOARD_TOP_NAV.find(nav => nav.key === key)
  if (!item) return true
  return canAccessAny(item.screens, role, customRoles)
}

export const isOrderOnly = role => role === ORDER_ONLY

/** Copy shown to users who cannot retrospectively edit a completed receipt. */
export const RETRO_RECEIPT_LOCKED_COPY = {
  title: 'Already receipted',
  message:
    'This receipt is locked. An Admin or Head Chef / GM can make retrospective corrections.',
}

/**
 * Retrospective receipt editing: Admin and Head Chef / GM by default.
 * Custom roles follow the retrospectiveReceiptEdit capability tick, so a
 * venue Admin can grant another role this ability.
 * @param {string} role
 * @param {Array} [customRoles=[]]
 */
export const canRetrospectivelyEditReceipts = (role, customRoles = []) =>
  hasCapability(
    normalizeDashboardVenueRole(role),
    VENUE_ROLE_CAPABILITY.RETROSPECTIVE_RECEIPT_EDIT,
    customRoles,
  )

export const canViewStockExpectedAndVariance = (role, customRoles = []) =>
  getStockRoleCapabilities(role, customRoles).canViewExpectedAndVariance

export const canUnlockStockTake = (role, customRoles = []) =>
  getStockRoleCapabilities(role, customRoles).canUnlockStockTake

export const canLockStockTake = (role, customRoles = []) =>
  getStockRoleCapabilities(role, customRoles).canLockStockTake

export const getStockVarianceVisibilityDecision = (role, customRoles = []) => {
  const { canViewExpectedAndVariance, hideSensitiveStockColumns } =
    getStockRoleCapabilities(role, customRoles)

  return {
    canViewSensitiveStockData: canViewExpectedAndVariance,
    hideSensitiveStockColumns,
  }
}

export default {
  canAccess,
  isBlocked,
  canAccessAny,
  isOrderOnly,
  canRetrospectivelyEditReceipts,
  RETRO_RECEIPT_LOCKED_COPY,
  canSeeSpecialPurchasesTab,
  canViewStockExpectedAndVariance,
  canUnlockStockTake,
  canLockStockTake,
  getStockRoleCapabilities,
  getStockRoleAccessBullets,
  getStockVarianceVisibilityDecision,
  normalizeStockRole,
  stockPermissionRowAllowsRole,
  PERMISSION_MATRIX,
  STOCK_VARIANCE_VISIBLE_ROLES,
  STOCK_UNLOCK_ROLES,
  STOCK_ROLE_PERMISSION_ROWS,
}
